import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ConfigProvider } from 'antd'
import ptBR from 'antd/locale/pt_BR'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAuthStore } from '@/stores/authStore'
import { AuthGuard, ContractGuard, PrecosGuard, PublicOnlyGuard } from '@/components/RouteGuards'
import { Layout, LayoutEmbutido } from '@/components/Layout'
import { Login } from '@/pages/Login'
import { SsoHyperpay } from '@/pages/SsoHyperpay'
import { Contrato } from '@/pages/Contrato'
import { Matricula } from '@/pages/Matricula'
import { Alunos } from '@/pages/Alunos'
import { Detalhes } from '@/pages/Detalhes'
import { Configuracoes } from '@/pages/Configuracoes'
import { UsuariosParceiro } from '@/pages/UsuariosParceiro'
import { Dashboard } from '@/pages/Dashboard'
import { SelecionarParceiro } from '@/pages/SelecionarParceiro'
import { Precos } from '@/pages/Precos'
import { PrecosEditor } from '@/pages/PrecosEditor'
import { AlterarSenha } from '@/pages/AlterarSenha'
import { Turmas } from '@/pages/Turmas'
import { TurmaDetalhe } from '@/pages/TurmaDetalhe'

const antdTheme = {
  token: {
    colorPrimary: '#1a3a5c',
    colorLink: '#ff6422',
    fontFamily: "'Rubik', system-ui, sans-serif",
    borderRadius: 8,
  },
}

export function App() {
  const checkSession = useAuthStore((s) => s.checkSession)

  // Na entrada vinda do Hyperpay não há sessão para conferir ainda: ela nasce da troca do login
  // token. Um checkSession em paralelo resolveria depois, sem token, e apagaria a sessão recém-criada.
  const entrandoPeloHyperpay = window.location.pathname === '/sso'

  useEffect(() => {
    if (entrandoPeloHyperpay) return
    checkSession()
  }, [checkSession, entrandoPeloHyperpay])

  return (
    <ConfigProvider theme={antdTheme} locale={ptBR}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnlyGuard>
                <Login />
              </PublicOnlyGuard>
            }
          />
          <Route path="/sso" element={<SsoHyperpay />} />
          <Route
            path="/contrato"
            element={
              <AuthGuard>
                <Contrato />
              </AuthGuard>
            }
          />
          <Route
            path="/selecionar-parceiro"
            element={
              <AuthGuard>
                <SelecionarParceiro />
              </AuthGuard>
            }
          />
          <Route
            path="/"
            element={
              <AuthGuard>
                <ContractGuard>
                  <Layout />
                </ContractGuard>
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="matricula" element={<Matricula />} />
            <Route path="alunos" element={<Alunos />} />
            <Route path="turmas" element={<Turmas />} />
            <Route path="turmas/:turmaId" element={<TurmaDetalhe />} />
            <Route path="detalhes/:matriculaId" element={<Detalhes />} />
            <Route
              path="precos"
              element={
                <PrecosGuard>
                  <Precos />
                </PrecosGuard>
              }
            />
            <Route
              path="precos/:targetId"
              element={
                <PrecosGuard>
                  <PrecosEditor />
                </PrecosGuard>
              }
            />
            <Route path="alterar-senha" element={<AlterarSenha />} />
            <Route path="configuracoes" element={<Configuracoes />} />
            <Route path="configuracoes/usuarios" element={<UsuariosParceiro />} />
          </Route>
          {/*
            Entrada embutida no Hyperpay: mesmos guards e mesmas telas, sem a moldura do painel. O
            acesso fica restrito a matricular e ver a matrícula recém-criada porque só essas duas
            rotas existem aqui dentro — o splat devolve qualquer outro caminho para a matrícula em
            vez de deixar escapar para o painel inteiro.
          */}
          <Route
            path="/embutido"
            element={
              <AuthGuard>
                <ContractGuard>
                  <LayoutEmbutido />
                </ContractGuard>
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="/embutido/matricula" replace />} />
            <Route path="matricula" element={<Matricula />} />
            <Route path="detalhes/:matriculaId" element={<Detalhes />} />
            <Route path="*" element={<Navigate to="/embutido/matricula" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer position="top-right" autoClose={4000} />
    </ConfigProvider>
  )
}

export default App
